import { useState, useEffect, useRef } from 'react';

/**
 * Returns a stable total count that only updates when no search/filter is active.
 * Prevents the denominator in "Showing X of Y" from dropping to 0 during search,
 * or flashing stale values when clearing search.
 *
 * On first load, accepts the first non-zero responseTotal regardless of search state
 * (handles bookmarked URLs with ?search=...). After initialization, only updates
 * when isSearching is false.
 *
 * @param responseTotal - The total from the current API response (changes with search)
 * @param isSearching - Whether a search/filter is currently active
 * @returns A stable total that only updates when isSearching is false
 */
export function useStableTotal(responseTotal: number, isSearching: boolean): number {
  const [stableTotal, setStableTotal] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && responseTotal > 0) {
      // Accept first non-zero response regardless of search state
      initialized.current = true;
      setStableTotal(responseTotal);
    } else if (initialized.current && !isSearching && responseTotal > 0) {
      setStableTotal(responseTotal);
    }
  }, [isSearching, responseTotal]);

  return stableTotal;
}
